/**
 * LA11 handoff (LA-12/13/15/19): one unmounted block for LA12's existing
 * Local Node placements. Both production mounts pass dispatchLocalAiPlan as
 * onDownloadPlan, which hands a user-confirmed, server-planned request to
 * the existing signed Bridge dispatch (F1). LA04 progress still arrives
 * through progress. Without that callback, downloads stay disabled;
 * planning is never dispatch. The mounting owner binds the catalog's
 * authorized device report to deviceId.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { LocalModelAdvisor } from './LocalModelAdvisor';
import type { AdvisorPrefill } from '../../api/localModelAdvisor';
import { AdultWorkspaceToggle } from './AdultWorkspaceToggle';
import {
  dispatchLocalAiDownload, fetchLocalAiCatalog, fetchLocalAiJobStatus, localAiBrowseUrl, localAiLicenceUrl,
  planLocalAiDownload, recordLocalAiAcceptance, recordLocalAiFriction, recordLocalAiMatureConfirmation,
  requestLocalAiHealth,
} from '../../api/localAi';
import type {
  LocalAiCatalog, LocalAiDispatchResult, LocalAiDownloadProgress, LocalAiDownloadRequest, LocalAiFrictionState,
  LocalAiItem,
} from '../../api/localAi';

export interface LocalAiIntakeProps {
  mode: 'setup' | 'settings';
  deviceId: string | null;
  onSkip?: () => void;
  /** LA-15: resolves only after the existing approval/dispatch owner accepts this request. */
  onDownloadPlan?: (deviceId: string, plan: LocalAiDownloadRequest) => Promise<LocalAiDispatchResult | void>;
  progress?: readonly LocalAiDownloadProgress[];
}

/** F1 (LA-15/19): the production onDownloadPlan binding for both mounts. */
export async function dispatchLocalAiPlan(deviceId: string, plan: LocalAiDownloadRequest): Promise<LocalAiDispatchResult> {
  return dispatchLocalAiDownload(deviceId, plan);
}

/** V88-R1: download status cadence. The first check runs immediately so the
 * queued state replaces "Waiting" at once; the bound covers long downloads. */
export const LOCAL_AI_DOWNLOAD_POLL_MS = 2000;
const LOCAL_AI_DOWNLOAD_POLLS = 900;
/** V88-R2: health receipt cadence. The catalog re-read below stays the
 * authority when the probe cannot be queued or the receipt never arrives. */
const LOCAL_AI_HEALTH_POLL_MS = 1000;
const LOCAL_AI_HEALTH_POLLS = 30;

const TERMINAL_QUEUE_STATES = ['finished', 'exhausted'];
const TERMINAL_PROGRESS_STATES = ['placed', 'failed', 'refused'];

const CONNECTORS = [
  { id: 'resolve', label: 'DaVinci Resolve' }, { id: 'ollama', label: 'Ollama' },
  { id: 'docker', label: 'Docker' }, { id: 'comfyui', label: 'ComfyUI' },
] as const;

const CAPABILITY_LABELS: Record<string, string> = {
  text_llm: 'Text and writing', image: 'Images', video: 'Video', tts: 'Voice',
  audio_processing: 'Audio processing', music_sfx: 'Music and sound effects', transcription: 'Transcription',
  model_3d: '3D models', unreal: 'Unreal projects', resolve_connector: 'Editing in Resolve', local_builders: 'Build tools',
};

function reasonText(code: string, fallback: string): string {
  if (code === 'needs_local_node') return 'Pair a local device and bring it online to use local AI.';
  if (code === 'needs_hf_token') return 'Add your own Hugging Face token in Auteur Bridge on your computer.';
  if (code === 'not_in_your_plan') return 'Your products do not include this local capability.';
  return fallback;
}

function sizeText(bytes: number | null): string {
  if (bytes === null) return 'Size unknown';
  if (bytes < 1024) return `${bytes} bytes`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)) - 1, units.length - 1);
  return `${(bytes / 1024 ** (index + 1)).toFixed(1)} ${units[index]}`;
}

function BrowseLink({ url }: { url: string | null }) {
  const safe = localAiBrowseUrl(url);
  return safe ? <a href={safe} target="_blank" rel="noopener noreferrer">Browse Hugging Face</a> : null;
}

function ModelCard({ model, deviceId, onDownloadPlan, progress, refresh }: {
  model: LocalAiItem;
  deviceId: string | null;
  onDownloadPlan: LocalAiIntakeProps['onDownloadPlan'];
  progress: readonly LocalAiDownloadProgress[];
  refresh: () => Promise<void>;
}) {
  const controlId = useId();
  const [checked, setChecked] = useState(!model.acceptance_required && model.licence_acceptance !== null);
  const [flow, setFlow] = useState<LocalAiFrictionState | null>(null);
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState<LocalAiDownloadRequest | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [polled, setPolled] = useState<LocalAiDownloadProgress | null>(null);
  const active = useRef(true);
  const running = useRef(false);
  const requestId = useRef<string | null>(null);
  const pollGeneration = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => () => {
    pollGeneration.current += 1;
    if (pollTimer.current !== null) { clearTimeout(pollTimer.current); pollTimer.current = null; }
  }, []);

  /** V88-R1: follow the kept dispatch job through the existing bridge job
   * status read, so this card renders downloading through completion and
   * failure even though no mount supplies the progress prop. The prop, when
   * present for the same request, still wins below. */
  function pollDownload(jobId: string, request: string) {
    const generation = ++pollGeneration.current;
    if (pollTimer.current !== null) { clearTimeout(pollTimer.current); pollTimer.current = null; }
    let attempts = 0;
    const step = async () => {
      if (!active.current || generation !== pollGeneration.current) return;
      if (attempts >= LOCAL_AI_DOWNLOAD_POLLS) return;
      attempts += 1;
      try {
        const status = await fetchLocalAiJobStatus(jobId);
        if (!active.current || generation !== pollGeneration.current) return;
        const entry = status.request_id === request ? status.progress : null;
        if (entry) setPolled(entry);
        if ((entry && TERMINAL_PROGRESS_STATES.includes(entry.state))
          || TERMINAL_QUEUE_STATES.includes(status.status)) return;
      } catch {
        // A transient read failure (or an older server without the status
        // route) never hides the submitted state; the bound below ends it.
      }
      if (!active.current || generation !== pollGeneration.current) return;
      pollTimer.current = setTimeout(() => { pollTimer.current = null; void step(); }, LOCAL_AI_DOWNLOAD_POLL_MS);
    };
    void step();
  }
  useEffect(() => {
    setChecked(!model.acceptance_required && model.licence_acceptance !== null);
  }, [model.acceptance_required, model.licence_acceptance?.accepted_at, model.licence_acceptance?.digest]);

  const licenceUrl = localAiLicenceUrl(model.licence?.url ?? null);
  const questionable = model.friction_required || model.licence?.tier === 'questionable' || model.licence?.tier === 'restricted';
  const frictionProof = flow ? flow.proof : model.friction_proof;
  const notice = flow?.notice ?? model.notice;
  const confirmation = flow?.confirmation_phrase ?? model.confirmation_phrase;
  const polledReport = plan && polled?.request_id === plan.request_id ? polled : null;
  const report = plan ? (progress.find((entry) => entry.request_id === plan.request_id) ?? polledReport ?? undefined) : undefined;
  const inProgress = report ? ['queued', 'downloading', 'verifying'].includes(report.state) : submitted;
  const metadataKnown = model.tested && model.licence?.id && licenceUrl && model.licence.version_or_date
    && model.licence.tier && model.file_ids !== null && model.store_id !== null
    && model.gated !== null && model.token_required !== null && model.mature !== null;
  const blocked = !deviceId ? { code: 'needs_local_node', text: reasonText('needs_local_node', '') }
    : model.state === 'disabled' ? { code: model.reason_code, text: reasonText(model.reason_code, model.reason_text) }
      : !metadataKnown ? { code: 'model_details_unknown', text: 'Download details are not available yet.' }
        : model.token_required && !model.token_present ? { code: 'needs_hf_token', text: reasonText('needs_hf_token', '') }
          : model.mature && (!model.adult_attested || !model.mature_attestation_ref)
            ? { code: 'adult_attestation_required', text: 'Adult attestation and confirmation of this model are required.' }
            : null;
  const downloadBlock = blocked
    ?? (!onDownloadPlan ? { code: 'download_unavailable', text: 'Downloads are not available here yet.' } : null)
    ?? (!checked || !model.licence_acceptance ? { code: 'licence_acceptance_required', text: 'Read and accept the licence before downloading.' } : null)
    ?? (questionable && !frictionProof ? { code: 'friction_required', text: 'Complete the licence review and typed confirmation first.' } : null)
    ?? (!model.download_ready ? { code: model.reason_code, text: reasonText(model.reason_code, model.reason_text) } : null);

  async function run(action: () => Promise<void>) {
    if (running.current || !active.current) return;
    running.current = true;
    setBusy(true);
    setError('');
    try {
      await action();
    } catch {
      // LA-5/10: never reflect raw transport/configuration/dispatch diagnostics.
      if (active.current) setError('This action could not be completed. Refresh the status and try again.');
    } finally {
      running.current = false;
      if (active.current) setBusy(false);
    }
  }

  async function review() {
    if (blocked) return;
    await run(async () => {
      const next = await recordLocalAiFriction({ model_id: model.item_id, action: 'show_notice' });
      if (!active.current) return;
      setFlow(next);
      setChecked(false);
      setPhrase('');
    });
  }

  async function acceptLicence(value: boolean) {
    if (blocked || !licenceUrl || !model.licence?.version_or_date || model.file_ids === null) return;
    if (!value && !questionable) {
      // LA-4: unchecked locally prevents use; the historical server acceptance
      // remains recorded. LA10 has no consent-deletion endpoint.
      setChecked(false);
      return;
    }
    await run(async () => {
      if (questionable) {
        const next = await recordLocalAiFriction({ model_id: model.item_id, action: 'check_licence', checked: value });
        if (!active.current) return;
        setFlow(next);
      }
      if (value) await recordLocalAiAcceptance({ model_id: model.item_id, file_ids: model.file_ids!,
        licence_url: licenceUrl, licence_version: model.licence!.version_or_date! });
      if (!active.current) return;
      setChecked(value);
      await refresh();
    });
  }

  async function confirm() {
    // LA-5/8: only the exact server phrase can leave this typed-confirmation
    // field. Pasting any other text cannot create a request.
    if (blocked || !checked || !confirmation || phrase !== confirmation) return;
    await run(async () => {
      const next = await recordLocalAiFriction({ model_id: model.item_id, action: 'confirm_phrase', phrase });
      if (!active.current) return;
      setFlow(next);
      await refresh();
    });
  }

  async function confirmMature() {
    // F3 (LA-11): supplies only the existing per-model confirmation for an
    // already adult-attested caller; the server re-reads every gate.
    await run(async () => {
      await recordLocalAiMatureConfirmation(model.item_id);
      await refresh();
    });
  }

  async function download() {
    if (downloadBlock || !deviceId || !onDownloadPlan || inProgress || report?.state === 'placed') return;
    await run(async () => {
      if (report?.state === 'failed' || report?.state === 'refused') requestId.current = null;
      // LA-19: an ordinary request id for the existing wire contract, reused
      // after an ambiguous submission failure; no media identity is minted.
      requestId.current ??= globalThis.crypto.randomUUID();
      const next = await planLocalAiDownload({
        request_id: requestId.current, model_id: model.item_id, file_ids: model.file_ids!, store_id: model.store_id!,
        licence_acceptance: model.licence_acceptance!, token_mode: model.token_required ? 'device_hf_token' : 'none',
        ...(questionable && frictionProof ? { friction: frictionProof } : {}),
        ...(model.mature && model.mature_attestation_ref ? { mature_attestation_ref: model.mature_attestation_ref } : {}),
      }, deviceId);
      if (!active.current) return;
      setPlan(next);
      setPolled(null);
      // V88-R1: keep the dispatch job id so the card can poll its status.
      const receipt = await onDownloadPlan(deviceId, next);
      const jobId = receipt && typeof receipt.job_id === 'string' && receipt.job_id ? receipt.job_id : null;
      if (active.current) setSubmitted(true);
      if (jobId && requestId.current && active.current) pollDownload(jobId, requestId.current);
    });
  }

  // LA-11: no name, link, licence, consent or action before legal attestation.
  if (model.mature && !model.adult_attested) {
    return <article className="settings-card" aria-disabled="true">
      <p data-reason-code={blocked?.code ?? model.reason_code}>
        {blocked?.text ?? reasonText(model.reason_code, model.reason_text)}
      </p>
    </article>;
  }

  const progressLabels = { queued: 'Waiting for your computer', downloading: 'Downloading', verifying: 'Checking the download',
    placed: 'Download complete', failed: 'Download failed', refused: 'Download could not start' };

  return <article className="settings-card" data-model-id={model.item_id} data-state={model.state}>
    <h6>{model.item_id}{model.installed ? ' · Found on your computer' : ''}</h6>
    <p>{sizeText(model.size_bytes)}</p>
    <p>Licence: {model.licence?.id ?? 'Unknown'}</p>
    {licenceUrl ? <p><a href={licenceUrl} target="_blank" rel="noopener noreferrer">{licenceUrl}</a></p> : <p>Licence link unavailable.</p>}
    {model.licence?.summary ? <p>{model.licence.summary}</p> : null}
    <p data-reason-code={blocked?.code ?? model.reason_code}>{blocked?.text ?? reasonText(model.reason_code, model.reason_text)}</p>
    {/* F3 (LA-11): shown only after the legal adult attestation; recording the
        per-model confirmation changes no gate or wording, it supplies it. */}
    {model.mature && model.adult_attested && !model.mature_attestation_ref ? <div>
      <button type="button" className="secondary" disabled={busy}
        onClick={() => void confirmMature()}>Confirm this model</button>
    </div> : null}

    {/* LA-8: verbatim server notice; no local notice or model-specific exception. */}
    {questionable ? <div className="notice">
      {notice ? <p style={{ whiteSpace: 'pre-wrap' }}>{notice}</p> : <p>The licence notice is not available yet.</p>}
      <button type="button" className="secondary" disabled={busy || !!blocked || !notice || !confirmation}
        onClick={() => void review()}>Review licence</button>
    </div> : null}

    <label htmlFor={`${controlId}-accept`}>
      <input id={`${controlId}-accept`} type="checkbox" checked={checked}
        disabled={busy || !!blocked || !licenceUrl || (questionable && (!flow || flow.stage === 'confirmed'))}
        onChange={(event) => void acceptLicence(event.target.checked)} />
      {questionable ? 'I have checked and accept the licence for my location' : 'I have read and accept this licence'}
    </label>

    {questionable ? <div>
      <label htmlFor={`${controlId}-confirmation`}>Type {confirmation ?? 'the model name shown after review'} to confirm</label>
      <input id={`${controlId}-confirmation`} type="text" autoComplete="off" value={phrase}
        disabled={busy || !!blocked || !checked || flow?.stage !== 'licence_checked'}
        onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }}
        onChange={(event) => setPhrase(event.target.value)} />
      <button type="button" className="secondary"
        disabled={busy || !!blocked || !checked || !confirmation || phrase !== confirmation || flow?.stage !== 'licence_checked'}
        onClick={() => void confirm()}>Confirm licence review</button>
    </div> : null}

    {/* LA-5: no token input. This command takes the user's token only on their own computer. */}
    {model.gated || model.token_required ? <div>
      <p>On your computer, open Auteur Bridge and run <code>auteur-bridge ai set-hf-token</code>.
        Enter your own Hugging Face token there when asked, then refresh this status.</p>
      <p>Hugging Face token on your computer: {model.token_present ? 'Present' : 'Not present'}</p>
    </div> : null}

    {/* LA-3/8: a questionable entry has no Download control until server confirmation. */}
    {!questionable || frictionProof ? <button type="button"
      disabled={busy || !!downloadBlock || inProgress || report?.state === 'placed'}
      onClick={() => void download()}>Download</button> : null}
    {downloadBlock ? <p data-reason-code={downloadBlock.code}>{downloadBlock.text}</p> : null}
    {error ? <p className="error" role="alert">{error}</p> : null}
    <div aria-live="polite">
      {report ? <>
        <p data-reason-code={report.reason_code ?? undefined}>{progressLabels[report.state]}</p>
        {model.size_bytes !== null && model.size_bytes > 0
          ? <progress aria-label={`Download progress for ${model.item_id}`} max={model.size_bytes} value={Math.min(report.bytes_done, model.size_bytes)} />
          : report.state === 'downloading' || report.state === 'verifying' ? <progress aria-label={`Download progress for ${model.item_id}`} /> : null}
        <p>{sizeText(report.bytes_done)} received</p>
      </> : <p>{busy ? 'Checking your request…' : submitted ? 'Download requested. Waiting for your computer.' : 'No download started.'}</p>}
    </div>
    <BrowseLink url={model.browse_url} />
  </article>;
}

function IntakeContent({ deviceId, onDownloadPlan, progress = [], skipped, showAdultToggle = false }: Omit<LocalAiIntakeProps, 'mode' | 'onSkip'> & { skipped: boolean; showAdultToggle?: boolean }) {
  const [advisorSelection, setAdvisorSelection] = useState<AdvisorPrefill[]>([]);
  const [catalog, setCatalog] = useState<LocalAiCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const active = useRef(true);
  const latestRead = useRef(0);
  const healthTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(async () => {
    const read = ++latestRead.current;
    setLoading(true);
    try {
      const next = await fetchLocalAiCatalog(deviceId, advisorSelection[0]?.store_id);
      if (active.current && read === latestRead.current) { setCatalog(next); setError(false); }
    } catch (failure) {
      if (active.current && read === latestRead.current) { setCatalog(null); setError(true); }
      throw failure;
    } finally {
      if (active.current && read === latestRead.current) setLoading(false);
    }
  }, [deviceId, advisorSelection]);
  const refreshFromDevice = useCallback(async () => {
    // F2 (LA-7/19): an explicit refresh first asks the selected device for
    // fresh connector health, then re-reads the catalog. The device answers
    // asynchronously, so its receipt is projected by a later read; the
    // catalog stays the authority if the probe cannot be queued.
    let healthJob: string | null = null;
    if (deviceId) {
      try {
        const receipt = await requestLocalAiHealth(deviceId);
        healthJob = receipt && typeof receipt.job_id === 'string' && receipt.job_id ? receipt.job_id : null;
      } catch { /* catalog read below reports the state */ }
    }
    try { await refresh(); } catch { /* the read already reports the failure */ }
    // V88-R2: without this bounded wait the user stays disabled even after
    // the device reports in. Re-read once the health receipt arrives (or the
    // bound lapses); an unreadable status leaves the first read authoritative.
    if (healthJob) {
      for (let attempt = 0; attempt < LOCAL_AI_HEALTH_POLLS; attempt += 1) {
        if (!active.current) return;
        await new Promise<void>((resolve) => { healthTimer.current = setTimeout(resolve, LOCAL_AI_HEALTH_POLL_MS); });
        healthTimer.current = null;
        if (!active.current) return;
        let done = false;
        try {
          const status = await fetchLocalAiJobStatus(healthJob);
          done = TERMINAL_QUEUE_STATES.includes(status.status);
        } catch { return; }
        if (done) break;
      }
      if (!active.current) return;
      try { await refresh(); } catch { /* the read already reports the failure */ }
    }
  }, [deviceId, refresh]);
  useEffect(() => {
    active.current = true;
    void refresh().catch(() => {});
    return () => {
      active.current = false; latestRead.current += 1;
      if (healthTimer.current !== null) { clearTimeout(healthTimer.current); healthTimer.current = null; }
    };
  }, [refresh]);

  const capabilities = catalog?.items.filter((item) => item.kind === 'capability') ?? [];
  // LA-11: defense in depth over the server's child projection, before grouping.
  const models = catalog?.items.filter((item) => item.kind === 'model' && !(catalog.context_kind === 'child' && item.mature)) ?? [];

  return <>
    {/* LA-7/10: four rows even during loading, errors, empty plans or Skip. */}
    <div className="settings-card-list" aria-label="Local connectors">
      {CONNECTORS.map(({ id, label }) => {
        const row = catalog?.items.find((item) => item.kind === 'connector' && item.item_id === id);
        const code = !deviceId ? 'needs_local_node' : row?.reason_code ?? 'connector_status_unknown';
        return <article key={id} className="settings-card" aria-disabled={!deviceId || !row || row.state === 'disabled'}>
          <h5>{label}</h5>
          <p>{!deviceId || row?.state === 'disabled' ? 'Unavailable' : row ? 'Connected' : 'Status unknown'}</p>
          <p data-reason-code={code}>{reasonText(code, row?.reason_text ?? 'This connection could not be checked yet.')}</p>
        </article>;
      })}
    </div>
    {showAdultToggle ? <AdultWorkspaceToggle onChanged={() => void refresh().catch(() => {})} /> : null}
    {error ? <p className="error" role="alert">Local AI status could not be loaded. Try again.</p> : null}
    {loading ? <p role="status">Checking local AI…</p> : null}
    <button type="button" className="secondary" disabled={loading} onClick={() => void refreshFromDevice().catch(() => {})}>Refresh status</button>
    {!skipped ? <>
      <LocalModelAdvisor deviceId={deviceId} onPlan={setAdvisorSelection} />
      {advisorSelection.length ? <>
        <p role="status">Selected models are highlighted below. Review their licences and consent before downloading.</p>
        {Array.from(new Set(advisorSelection.map(item => item.store_id))).map(store =>
          <button key={store} type="button" className="secondary" onClick={() => setAdvisorSelection(items => [
            ...items.filter(item => item.store_id === store), ...items.filter(item => item.store_id !== store),
          ])}>Review store {store}</button>)}
      </> : null}
      {capabilities.map((capability) => <section key={capability.item_id} className="settings-subsection"
        aria-label={CAPABILITY_LABELS[capability.item_id] ?? capability.item_id}>
        <h5>{CAPABILITY_LABELS[capability.item_id] ?? capability.item_id}</h5>
        <p data-reason-code={!deviceId ? 'needs_local_node' : capability.reason_code}>
          {!deviceId ? reasonText('needs_local_node', '') : reasonText(capability.reason_code, capability.reason_text)}
        </p>
        <div className="settings-card-list">
          {models.filter((model) => model.tested && model.capabilities.includes(capability.item_id)).map((model) =>
            <div key={model.item_id} data-advisor-selected={advisorSelection.some(item => item.model_id === model.item_id && item.device_id === deviceId && item.store_id === model.store_id)}>
            {advisorSelection.some(item => item.model_id === model.item_id && item.device_id === deviceId && item.store_id === model.store_id)
              ? <p role="status">Selected for licence review</p> : null}
            <ModelCard key={JSON.stringify([model.item_id, model.connector, model.licence, model.file_ids, model.store_id,
              model.notice, model.adult_attested, model.mature_attestation_ref])}
              model={model} deviceId={deviceId} onDownloadPlan={onDownloadPlan} progress={progress} refresh={refresh} /></div>)}
        </div>
        <BrowseLink url={capability.browse_url} />
      </section>)}
      {models.filter((model) => !model.tested).map((model) => <article className="settings-card" key={`${model.item_id}:${model.connector}`}>
        <h5>{model.item_id}{model.installed ? ' · Found on your computer' : ''}</h5>
        <p data-reason-code={!deviceId ? 'needs_local_node' : model.reason_code}>
          {!deviceId ? reasonText('needs_local_node', '') : reasonText(model.reason_code, model.reason_text)}
        </p>
        <BrowseLink url={model.browse_url} />
      </article>)}
    </> : null}
  </>;
}

export function LocalAiIntake({ mode, deviceId, onSkip, onDownloadPlan, progress }: LocalAiIntakeProps) {
  const [skipped, setSkipped] = useState(false);
  const showSkipped = mode === 'setup' && skipped;
  return <section className="settings-panel-content" aria-label="Local AI">
    <div className="settings-card-heading">
      <h4>Local AI</h4>
      {/* LA-12: optional inside the existing block, including loading and errors. */}
      {mode === 'setup' ? <button type="button" className="secondary" onClick={() => { setSkipped(true); onSkip?.(); }}>Skip</button> : null}
    </div>
    {showSkipped ? <p>You can set up local AI later in Settings. <button type="button" className="secondary"
      onClick={() => setSkipped(false)}>Set up local AI</button></p> : null}
    <IntakeContent key={deviceId ?? ''} deviceId={deviceId} onDownloadPlan={onDownloadPlan} progress={progress} skipped={showSkipped}
      showAdultToggle={mode !== 'setup'} />
  </section>;
}
